import React from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Select,
  MenuItem,
  Link,
  Tooltip,
} from "@mui/material";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import axios from "../../api/axios";

export const ETIQUETA_TIPO = {
  Representante: "Representante oficial",
  Temporal: "Representante Temporal",
  Deshabilitado: "Representante Deshabilitado",
};

export const ETIQUETA_TIPO_PLURAL = {
  Representante: "Representantes oficiales",
  Temporal: "Representantes Temporales",
  Deshabilitado: "Representantes Deshabilitados",
};

const COLOR_ACCION = { Aprobar: "#2E7D32", Rechazar: "#F4511E" };
const AJUSTE_ALINEAR_FIRMA = "Sin firma MINEDUC";
const ORDEN_ACCION = { Rechazar: 0, Aprobar: 1 };

const prioridadDocumento = (doc) =>
  doc.acciones?.length ? ORDEN_ACCION[doc.accion_sugerida] ?? 2 : 3;

export const accionDocumentos = (origen, destino) => {
  if (destino === "Deshabilitado") return "deshabilitar";
  if (destino === "Representante" && origen === "Temporal") return "oficial";
  return null;
};

export const textosCambio = (destino, masivo) => {
  if (destino === "Deshabilitado") {
    return {
      pregunta: masivo ? "¿Está seguro que quiere pasarlos a Deshabilitado?" : "¿Está seguro que quiere Deshabilitar?",
      descripcion: "Esta acción le impedirá al usuario entrar a la plataforma, pero puede ser revertido si usted Rol MINEDUC vuelve a cambiar el Tipo de Representante.",
      boton: "OK, pasar a Deshabilitado",
    };
  }
  return {
    pregunta: masivo ? "¿Está seguro que quiere pasarlos a Representante oficial?" : "¿Está seguro que quiere pasar a Representante oficial?",
    descripcion: "Esta acción permitirá que el Representante pueda realizar firmas de documentos, cambiar datos del establecimiento y agregar/deshabilitar a otros representantes.",
    boton: "OK, cambiar a Representante oficial",
  };
};

export const claveDocumento = (doc) => `${doc.tipo_documento}_${doc.id}`;

const abrirDetalle = (doc) => window.open(`/${doc.tipo_documento}_detalle/${doc.id}`, "_blank");

export const decisionesIniciales = (documentos) =>
  documentos.reduce((acc, doc) => {
    if (doc.acciones?.length) acc[claveDocumento(doc)] = doc.accion_sugerida;
    return acc;
  }, {});

export const listaDecisiones = (documentos, decisiones) =>
  documentos
    .filter((doc) => doc.acciones?.length && decisiones[claveDocumento(doc)])
    .map((doc) => ({ tipo_documento: doc.tipo_documento, id: doc.id, accion: decisiones[claveDocumento(doc)] }));

export const detalleError = (err, respaldo) =>
  err?.response?.data?.detail || err?.message || respaldo;

export const ejecutarCambioTipo = (origen, destino, run, rbd) => {
  if (destino === "Deshabilitado") return axios.disableRepresentante(run, rbd);
  if (destino === "Representante" && origen === "Deshabilitado") return axios.enableRepresentante(run, rbd);
  if (destino === "Representante") return axios.temporalToRepresentante(run, rbd);
  return Promise.reject(new Error(`Acción desconocida: ${destino}`));
};

export const obtenerImpacto = async (accion, representantes) => {
  const response = await axios.getImpactoCambioRepresentantes({ accion, representantes });
  return response.data.documentos || [];
};

export const regularizarDocumentos = async (accion, representantes, decisiones = []) => {
  const response = await axios.regularizarDocumentosRepresentantes({ accion, representantes, decisiones });
  return response.data.resultados || [];
};

const estadoResultante = (doc, valor) =>
  valor === "Aprobar" ? doc.estado_si_aprueba || "Aprobado" : "Rechazado";

const estiloChip = (valor) => ({
  bgcolor: COLOR_ACCION[valor],
  color: "#fff",
  borderRadius: "12px",
  px: 1.5,
  py: 0.25,
  fontSize: "0.625rem",
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  lineHeight: "18px",
  display: "inline-block",
});

function ChipAccion({ valor }) {
  return <Box component="span" sx={estiloChip(valor)}>{valor}</Box>;
}

function CeldaAccion({ doc, decisiones, onDecision }) {
  const acciones = doc.acciones || [];
  if (!acciones.length) {
    return (
      <Typography sx={{ fontSize: "0.75rem", color: "#4D4D4D" }}>
        {doc.resultado === AJUSTE_ALINEAR_FIRMA ? "Queda con firma de Representante oficial" : `Pasa a ${doc.resultado}`}
      </Typography>
    );
  }
  const valor = decisiones?.[claveDocumento(doc)] || doc.accion_sugerida;
  const titulo = `Quedará en: ${estadoResultante(doc, valor)}`;

  if (acciones.length > 1 && onDecision) {
    return (
      <Tooltip title={titulo} placement="left">
        <Select
          size="small"
          variant="standard"
          disableUnderline
          value={valor}
          onChange={(e) => onDecision(claveDocumento(doc), e.target.value)}
          IconComponent={UnfoldMoreIcon}
          renderValue={(v) => <ChipAccion valor={v} />}
          sx={{
            "& .MuiSelect-select": { display: "flex", alignItems: "center", py: 0.5, pr: "24px !important" },
            "& .MuiSelect-icon": { fontSize: "1.1rem", color: "#111" },
          }}
        >
          {acciones.map((opcion) => (
            <MenuItem key={opcion} value={opcion} sx={{ fontSize: "0.875rem" }}>
              {opcion}
            </MenuItem>
          ))}
        </Select>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={titulo} placement="left">
      <Box component="span"><ChipAccion valor={valor} /></Box>
    </Tooltip>
  );
}

export function TablaDocumentosImpacto({ documentos, mostrarRbd = false, mostrarDetalle = false, decisiones, onDecision }) {
  const mostrarAccion = !mostrarDetalle;
  const hayDecisiones = documentos.some((doc) => doc.acciones?.length);
  return (
    <TableContainer
      component={Paper}
      elevation={2}
      sx={{ maxHeight: "200px", overflowY: "auto", my: 2, textAlign: "left" }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell>Tipo</TableCell>
            {mostrarRbd && <TableCell>RBD</TableCell>}
            {mostrarRbd && mostrarDetalle && <TableCell>RUN</TableCell>}
            <TableCell>ID Folio</TableCell>
            <TableCell>Operador</TableCell>
            <TableCell>Estado</TableCell>
            {mostrarAccion && (
              <TableCell sx={{ lineHeight: 1.2 }}>{hayDecisiones ? "Aprobar o Rechazar" : "Resultado"}</TableCell>
            )}
            {mostrarDetalle && <TableCell>Resultado</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {documentos.map((doc) => (
            <TableRow key={`${claveDocumento(doc)}_${doc.rut || ""}`}>
              <TableCell sx={{ fontSize: "0.75rem" }}>{doc.tipo}</TableCell>
              {mostrarRbd && <TableCell sx={{ fontSize: "0.75rem" }}>{doc.rbd}</TableCell>}
              {mostrarRbd && mostrarDetalle && <TableCell sx={{ fontSize: "0.75rem" }}>{doc.rut || "-"}</TableCell>}
              <TableCell sx={{ fontSize: "0.75rem" }}>
                <Link
                  component="button"
                  type="button"
                  title="Ver documento"
                  onClick={() => abrirDetalle(doc)}
                  sx={{ fontSize: "0.75rem", color: "#111", textDecoration: "underline" }}
                >
                  {doc.folio}
                </Link>
              </TableCell>
              <TableCell sx={{ fontSize: "0.75rem" }}>{doc.operador || "-"}</TableCell>
              <TableCell sx={{ fontSize: "0.75rem" }}>{doc.estado}</TableCell>
              {mostrarAccion && (
                <TableCell>
                  <CeldaAccion doc={doc} decisiones={decisiones} onDecision={onDecision} />
                </TableCell>
              )}
              {mostrarDetalle && (
                <TableCell sx={{ fontSize: "0.75rem", color: doc.status === "Fallido" ? "error.main" : "inherit" }}>
                  {doc.status === "Fallido" ? doc.detail || "Fallido" : doc.resultado}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function ImpactoDocumentos({ cargando, error, documentos, decisiones, onDecision, masivo = false }) {
  if (cargando) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, my: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Revisando documentos sin cerrar…</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" sx={{ color: "error.main", my: 2 }}>
        No fue posible revisar los documentos sin cerrar: {error}
      </Typography>
    );
  }

  const conAccion = documentos.filter((doc) => doc.acciones?.length);
  const ordenados = [...documentos].sort((a, b) => prioridadDocumento(a) - prioridadDocumento(b));
  const alineados = documentos.filter((doc) => doc.resultado === "Sin firma MINEDUC").length;
  const aprobadosSinCierre = conAccion.reduce((acc, doc) => {
    const valor = decisiones?.[claveDocumento(doc)] || doc.accion_sugerida;
    const estado = estadoResultante(doc, valor);
    if (valor === "Aprobar" && estado !== "Aprobado") acc[estado] = (acc[estado] || 0) + 1;
    return acc;
  }, {});

  return (
    <>
      {documentos.length > 0 && (
        <>
          <Typography sx={{ fontWeight: 700, color: "#111" }}>
            {masivo ? "Estos Representantes tienen documentos sin cerrar:" : "Este Representante tiene documentos sin cerrar:"}
          </Typography>
          {conAccion.length > 0 && (
            <Box sx={{ maxWidth: "440px", margin: "0 auto" }}>
              <Typography sx={{ color: "error.main", mt: 1 }}>
                Por favor revisar y cerrar como aprobado o rechazado los documentos pendientes
              </Typography>
            </Box>
          )}
          <TablaDocumentosImpacto
            documentos={ordenados}
            mostrarRbd={masivo}
            decisiones={decisiones}
            onDecision={onDecision}
          />
          {Object.entries(aprobadosSinCierre).map(([estado, total]) => (
            <Typography key={estado} variant="body2" sx={{ color: "#4D4D4D", mb: 1 }}>
              {total === 1
                ? `1 documento aprobado seguirá su flujo en ${estado} y no volverá a requerir firma MINEDUC.`
                : `${total} documentos aprobados seguirán su flujo en ${estado} y no volverán a requerir firma MINEDUC.`}
            </Typography>
          ))}
        </>
      )}
      {alineados > 0 && (
        <Typography variant="body2" sx={{ color: "#4D4D4D", mb: 1 }}>
          {alineados === 1
            ? "1 documento pendiente de firma quedará asociado al Representante oficial y no requerirá firma MINEDUC."
            : `${alineados} documentos pendientes de firma quedarán asociados al Representante oficial y no requerirán firma MINEDUC.`}
        </Typography>
      )}
    </>
  );
}
